import React, { useContext, type JSX } from 'react';
import columnCaptionQuery from '../../../../../../../../Queries/EkonomiskAnalysColumnCaptionQuery/EkonomiskAnalysColumnCaptionQuery.gql';
import type {
  EkonomiskAnalysColumnCaptionQuery as IColumnCaptionQueryData,
  EkonomiskAnalysColumnCaptionQueryVariables as IColumnCaptionQueryVariables,
  EkonomiskAnalysColumnCaptionQuery_ekanalyskolumninfoperanalys_columns,
} from '../../../../../../../../Queries/EkonomiskAnalysColumnCaptionQuery/_QueryTypes/EkonomiskAnalysColumnCaptionQuery';
import { Query } from '@apollo/client/react/components';
import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { EkonomiskAnalysSideMenuContext } from '../../../../../../Context/EkonomiskAnalysSideMenuContext';
import { ekanalys_ekanalys_columns } from '../../../../../../../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import {
  colToInputCol,
  datesToTimeInterval,
  datesToPartialTimeInterval,
} from '../../../../../../../../Utils/convertUtils';
import { ISelectedAnalys } from '../../../../../../../../EkonomiskAnalysPage';
import { ILocalAnalys } from '../../../../../../../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { INewColumnData } from '../../EkonomiskAnalysEditColumnDialog/EkonomiskAnalysEditColumnDialog';
import { IEkonomiskAnalysFilledDates } from '../../../../../../../AnalysSettingsHandler/AnalysSettingsHandler';
import { EkonomiskAnalysStandardColumnQuery_ekanalysstandardcolumns } from '../EkonomiskAnalysStandardColumnQuery/_QueryTypes/EkonomiskAnalysStandardColumnQuery';

interface IResult {
  loading: boolean;
  error?: ApolloError;
  columns?: EkonomiskAnalysColumnCaptionQuery_ekanalyskolumninfoperanalys_columns[];
  refetch: EditColumnCaptionQueryRefetchType;
  variables: IColumnCaptionQueryVariables | undefined;
}

interface IProps {
  columns?: ekanalys_ekanalys_columns[];
  standardColumns?: EkonomiskAnalysStandardColumnQuery_ekanalysstandardcolumns[];
  newColumnData: INewColumnData;
  children: (result: IResult) => JSX.Element;
}

export type EditColumnCaptionQueryRefetchType = (
  variables?: IColumnCaptionQueryVariables
) => Promise<ApolloQueryResult<IColumnCaptionQueryData>>;

export const getColumnCaptionQueryVariables = (
  selectedAnalys: ISelectedAnalys | null,
  newAnalysSettings: ILocalAnalys | null,
  column?: ekanalys_ekanalys_columns[]
): IColumnCaptionQueryVariables => {
  const today = new Date();

  return {
    analys: {
      name:
        selectedAnalys != null && selectedAnalys.analys.name
          ? selectedAnalys.analys.name
          : '',
      columns: column ? column.map(colToInputCol) : [],
      sums: null,
    },
    codePrivilegeId: selectedAnalys ? selectedAnalys.codePrivilege.id : '',
    tidsintervall:
      newAnalysSettings &&
      newAnalysSettings.dates.from.year &&
      newAnalysSettings.dates.from.month &&
      newAnalysSettings.dates.to.year &&
      newAnalysSettings.dates.to.month
        ? datesToTimeInterval(
            newAnalysSettings.dates as IEkonomiskAnalysFilledDates
          )
        : {
            fromYear: today.getFullYear(),
            fromMonth: today.getMonth() + 1,
            fromDay: 1,
            toYear: today.getFullYear(),
            toMonth: today.getMonth() + 1,
            toDay: 1,
          },
  };
};

const EkonomiskAnalysEditColumnCaptionQuery: React.FunctionComponent<
  IProps
> = props => {
  const { selectedAnalys, newAnalysSettings } = useContext(
    EkonomiskAnalysSideMenuContext
  );
  const variables = props.newColumnData
    ? {
        analys: {
          columns: props.columns ? props.columns.map(colToInputCol) : [],
          name: 'Namnlös kolumn',
          sums: null,
        },
        codePrivilegeId: props.newColumnData.codePrivilegeId,
        tidsintervall: datesToPartialTimeInterval(
          props.newColumnData.dates,
          props.newColumnData.isInterval
        ),
      }
    : getColumnCaptionQueryVariables(
        selectedAnalys,
        newAnalysSettings,
        props.columns
      );

  return (
    <Query<IColumnCaptionQueryData, IColumnCaptionQueryVariables>
      query={columnCaptionQuery}
      skip={props.columns == null}
      variables={variables}
    >
      {({ loading, data, error, refetch, variables }) =>
        props.children({
          loading,
          error,
          columns:
            data &&
            data.ekanalyskolumninfoperanalys &&
            data.ekanalyskolumninfoperanalys.columns
              ? data.ekanalyskolumninfoperanalys.columns
              : undefined,
          refetch,
          variables,
        })
      }
    </Query>
  );
};

export { IColumnCaptionQueryData, IColumnCaptionQueryVariables };
export default EkonomiskAnalysEditColumnCaptionQuery;
